import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../config/firebase';

const RevisionPaymentModal34 = () => {
  const {
    showRevisionPaymentModal,
    setShowRevisionPaymentModal,
    revisionProject,
    revisionData,
    setProjects,
    showNotification,
    typeNames,
    documentTypes
  } = useAppContext();

  const [processing, setProcessing] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!showRevisionPaymentModal) {
      setAcceptedTerms(false);
      setProcessing(false);
      setPaymentMethod('card');
    }
  }, [showRevisionPaymentModal]);

  if (!showRevisionPaymentModal || !revisionProject || !revisionData) return null;

  // Only handle Stage 3 & 4 projects
  if (revisionProject?.stage < 3) return null;

  // Upload files to Firebase Storage
  const uploadFilesToStorage = async (projectId, files) => {
    console.log('=== Starting file upload to Firebase Storage (Payment Modal) ===');
    console.log('Project ID:', projectId);
    console.log('Number of files to upload:', files.length);
    
    const uploadedDocs = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storagePath = `projects/${projectId}/revision-documents/${timestamp}-${safeName}`;
        const storageRef = ref(storage, storagePath);
        
        console.log(`Uploading file ${i + 1}/${files.length}: ${file.name}`);
        console.log('Storage path:', storagePath);
        
        const snapshot = await uploadBytes(storageRef, file);
        console.log('Upload snapshot:', snapshot);
        
        const downloadURL = await getDownloadURL(snapshot.ref);
        console.log('Download URL obtained:', downloadURL);
        
        uploadedDocs.push({
          name: file.name,
          size: file.size,
          type: file.type,
          uploadDate: new Date().toISOString(),
          url: downloadURL,
          storagePath: snapshot.ref.fullPath
        });
        
        console.log(`✅ File ${i + 1} uploaded successfully:`, file.name);
      } catch (error) {
        console.error(`❌ Error uploading file ${file.name}:`, error);
        console.error('Error details:', error.message);
        
        // Still track the file even if upload failed
        uploadedDocs.push({
          name: file.name,
          size: file.size,
          type: file.type,
          uploadDate: new Date().toISOString(),
          url: null,
          error: error.message
        });
      }
    }
    
    console.log('=== File upload complete (Payment Modal) ===');
    console.log('Successfully uploaded:', uploadedDocs.filter(d => d.url).length);
    console.log('Failed uploads:', uploadedDocs.filter(d => !d.url).length);
    
    return uploadedDocs;
  };

  const handlePayment = async () => {
    if (!acceptedTerms) {
      showNotification("Terms Required", "Please accept the terms and conditions to proceed with payment", "error");
      return;
    }

    setProcessing(true);
    console.log('=== PAYMENT PROCESSING STARTED ===');
    console.log('Revision data:', revisionData);
    console.log('Supporting files:', revisionData.supportingFiles?.length || 0);
    
    // Simulate payment processing for Stage 3/4
    await new Promise(resolve => setTimeout(resolve, 2000));

    try {
      // CRITICAL: Upload supporting files if any exist
      let uploadedDocuments = [];
      if (revisionData.supportingFiles && revisionData.supportingFiles.length > 0) {
        console.log(`Uploading ${revisionData.supportingFiles.length} supporting files after payment...`);
        uploadedDocuments = await uploadFilesToStorage(
          revisionProject.id, 
          revisionData.supportingFiles
        );
        console.log('Files uploaded after payment:', uploadedDocuments);
      } else {
        console.log('No supporting files to upload');
      }

      // Update document revision tracking
      const updatedTracking = { ...revisionProject.documentRevisionTracking };
      if (!updatedTracking[revisionData.documentType]) {
        updatedTracking[revisionData.documentType] = {
          revisionsUsed: 0,
          maxIncluded: 1,
          documents: []
        };
      }
      updatedTracking[revisionData.documentType].revisionsUsed = revisionData.revisionNumber;
      
      // Create payment key
      const paymentKey = `${revisionData.documentType}_revision${revisionData.revisionNumber}PaymentStatus`;
      
      // Update project with payment and revision request
      const updatedProject = {
        ...revisionProject,
        [paymentKey]: 'paid',
        status: `revision-${revisionData.revisionNumber}-requested`,
        documentRevisionTracking: updatedTracking,
        paymentHistory: [
          ...(revisionProject.paymentHistory || []),
          {
            amount: revisionData.fee,
            date: new Date().toISOString(),
            type: 'revision',
            documentType: revisionData.documentType,
            revisionNumber: revisionData.revisionNumber,
            description: `Revision ${revisionData.revisionNumber} for ${revisionData.documentTypeLabel}`
          }
        ],
        communications: [
          ...(revisionProject.communications || []),
          // Revision request submission
          {
            type: 'client',
            sender: revisionProject.client,
            message: `Revision ${revisionData.revisionNumber} requested for ${revisionData.documentTypeLabel}: ${revisionData.feedback}${uploadedDocuments.length > 0 ? ` (${uploadedDocuments.length} supporting file(s) attached)` : ''}`,
            timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
          },
          // Payment confirmation
          {
            type: 'system',
            sender: 'planAIdesign System',
            message: `Payment of $${revisionData.fee} received for Revision ${revisionData.revisionNumber} - ${revisionData.documentTypeLabel}.`,
            timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
          },
          // Work commencement
          {
            type: 'architect',
            sender: 'planAIdesign',
            message: `Thank you for your payment. We have received your revision request for ${revisionData.documentTypeLabel} and will begin working on it. Expected completion: 3-5 business days.`,
            timestamp: new Date(Date.now() + 2000).toISOString().slice(0, 16).replace('T', ' ')
          }
        ],
        revisionDocuments: [
          ...(revisionProject.revisionDocuments || []),
          {
            documentType: revisionData.documentType,
            documentTypeLabel: revisionData.documentTypeLabel,
            revisionNumber: revisionData.revisionNumber,
            requirements: revisionData.feedback,
            files: uploadedDocuments, // Now using uploaded documents with URLs
            uploadDate: new Date().toISOString(),
            isAdditionalRevision: true
          }
        ]
      };

      console.log('Saving updated project to Firebase...');
      console.log('Project ID:', revisionProject.id);
      console.log('Revision documents count:', updatedProject.revisionDocuments.length);
      
      // Save to Firebase
      await setDoc(doc(db, 'projects', revisionProject.id), updatedProject);
      console.log('✅ Project saved to Firebase successfully');

      // Update local state
      setProjects(prev => prev.map(p => 
        p.id === revisionProject.id ? updatedProject : p
      ));
      
      showNotification(
        "Payment & Revision Submitted", 
        `Payment processed successfully. Your revision request for ${revisionData.documentTypeLabel} has been submitted${uploadedDocuments.length > 0 ? ` with ${uploadedDocuments.length} supporting file(s)` : ''}.`, 
        "success"
      );
      
      console.log('=== PAYMENT PROCESSING COMPLETE ===');
      setProcessing(false);
      setShowRevisionPaymentModal(false);
    } catch (error) {
      console.error('❌ Payment/Upload error:', error);
      console.error('Error details:', error.message);
      setProcessing(false);
      showNotification("Error", "Failed to process payment or upload files. Please try again.", "error");
    }
  };

  // Get document types for current stage
  const getDocumentTypeInfo = () => {
    const docTypes = revisionProject.stage === 3 ? documentTypes?.stage3 : documentTypes?.stage4;
    return docTypes?.[revisionData.documentType] || revisionData.documentTypeLabel;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-8 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold">
            Additional Revision Payment
          </h3>
          <button 
            onClick={() => setShowRevisionPaymentModal(false)} 
            className="text-2xl text-slate-400 hover:text-slate-600"
          >✕</button>
        </div>

        <div className="mb-6">
          <div className="bg-purple-50 p-4 rounded-lg mb-4">
            <h4 className="font-semibold text-lg mb-2">{revisionProject.name}</h4>
            <p className="text-sm text-gray-600">
              {typeNames[revisionProject.type]} | Stage {revisionProject.stage}
            </p>
            <div className="mt-3 pt-3 border-t border-purple-200">
              <p className="text-sm font-semibold text-gray-600 mb-1">
                Document Type: {revisionData.documentTypeLabel}
              </p>
              <p className="text-sm font-semibold text-gray-600 mb-1">
                Revision {revisionData.revisionNumber} (Additional)
              </p>
              <p className="text-2xl font-bold text-purple-600">
                ${revisionData.fee}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Additional revision fee for {revisionData.documentTypeLabel}
              </p>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Payment Method</label>
            <div className="space-y-2">
              <label className="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  value="card"
                  checked={paymentMethod === 'card'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium">Credit/Debit Card</div>
                  <div className="text-xs text-gray-600">Secure payment via card</div>
                </div>
              </label>
              <label className="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  value="bank"
                  checked={paymentMethod === 'bank'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium">Bank Transfer</div>
                  <div className="text-xs text-gray-600">Direct bank transfer</div>
                </div>
              </label>
            </div>
          </div>

          {/* Test Mode Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">Test Mode</p>
                <p>This is a simulated payment for testing. In production, this would integrate with a real payment processor.</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg mb-4 border border-yellow-200">
            <h5 className="font-semibold text-sm mb-2 text-yellow-800">Why is there a fee?</h5>
            <p className="text-xs text-gray-700">
              {revisionData.documentType !== 'architectural' && revisionData.documentType !== 'architectural-detailed' ? (
                <>Consultant documents include 1 revision at no additional cost. 
                After the first revision, consultant documents have unlimited additional revisions at no extra charge. 
                This fee covers coordination and management costs.</>
              ) : (
                <>Architectural documents include 1 revision at no additional cost. 
                This is revision {revisionData.revisionNumber} for {revisionData.documentTypeLabel}, 
                which requires the additional revision fee as outlined in your original agreement.</>
              )}
            </p>
          </div>

          {revisionData.feedback && (
            <div className="bg-blue-50 p-4 rounded-lg mb-4 border border-blue-200">
              <h5 className="font-semibold text-sm mb-2 text-blue-800">Your Revision Requirements</h5>
              <p className="text-xs text-gray-700 whitespace-pre-wrap">{revisionData.feedback}</p>
              {revisionData.supportingFiles && revisionData.supportingFiles.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs font-semibold text-gray-600">Supporting files to be uploaded:</p>
                  <ul className="text-xs text-gray-600 mt-1">
                    {revisionData.supportingFiles.map((file, idx) => (
                      <li key={idx}>• {typeof file === 'object' ? file.name : file} ({typeof file === 'object' ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'size unknown'})</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Terms and Conditions */}
          <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h5 className="font-semibold text-sm mb-2">Revision Payment Terms</h5>
            <div className="text-xs text-gray-700 space-y-2 mb-3">
              <p>• Payment must be completed before revision work can begin.</p>
              <p>• Once payment is processed, your revision request will be automatically submitted.</p>
              {revisionData.supportingFiles && revisionData.supportingFiles.length > 0 && (
                <p>• Your {revisionData.supportingFiles.length} supporting file(s) will be uploaded after payment.</p>
              )}
              <p>• Revision work will commence within 1 business day of submission.</p>
              <p>• Timeline for completion: 3-5 business days for Stage {revisionProject.stage}</p>
              <p>• This fee covers one complete revision round for {revisionData.documentTypeLabel}.</p>
              <p>• Further revisions for this document type will incur additional fees.</p>
              <p>• No refunds once revision work has commenced.</p>
            </div>
            
            <label className="flex items-start gap-2 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5"
                disabled={processing}
              />
              <span className="text-xs text-gray-700">
                I understand this is an additional revision fee for {revisionData.documentTypeLabel} and accept the terms.
              </span>
            </label>
          </div>
        </div>

        <div className="flex gap-4 mt-6">
          <button 
            onClick={() => setShowRevisionPaymentModal(false)}
            className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600"
            disabled={processing}
          >
            Cancel
          </button>
          <button 
            onClick={handlePayment}
            disabled={processing || !acceptedTerms}
            className="flex-1 bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:bg-gray-400"
          >
            {processing ? 'Processing Payment & Uploading Files...' : `Pay $${revisionData.fee}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RevisionPaymentModal34;